import { FC, useMemo } from "react";
import { useQuery } from "react-query";

import {
  Bundle,
  Client,
  Product,
  Claiming,
  Invoice,
  UserExtension,
  BundleExtension,
  Airdrops,
} from "@prisma/client";

import SectionChrisLostHisItems from "../SectionChrisLostHisItems";
import SectionWeCountdown from "../SectionWeCountdown";
import SectionVideo from "../SectionVideo";
import SectionVideoModal from "../SectionVideoModal";
import SectionFarokhLostHisItems from "../SectionFarokhLostHisItems";
import SectionCollectAddresses from "../SectionCollectAddresses";
import SectionEmail from "../SectionEmail";
import SectionAirdrop from "../SectionAirdrop";
import SectionSolAirdrop from "../SectionSolAirdrop";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";

import { Event } from "apiIntegrations/poap/Poap.interface";

import { useAppSelector } from "redux/store";
import { selectAddress, selectCustodialAddress } from "redux/slices/metamask";

import SectionLinks from "sections/itemsPage/SectionLinks";
import SectionTicket from "sections/itemsPage/SectionTicket";
import SectionPoapCreate from "sections/itemsPage/SectionPoapCreate";
import SectionDescription from "sections/itemsPage/SectionDescription";
import SectionPoap from "sections/itemsPage/SectionPoap";
import SectionClaimingPhysical from "sections/itemsPage/SectionClaimingPhysical";
import SectionStorePartnerContact from "sections/itemsPage/SectionStorePartnerContact";
import SectionClaimingDecentraland from "sections/itemsPage/SectionClaimingDecentraland";
import SectionMint from "sections/itemsPage/SectionMint";
import SectionAttributes from "sections/itemsPage/SectionAttributes";
import SectionHistory, { ItemHistory } from "sections/itemsPage/SectionHistory";
import SectionProduct from "sections/itemsPage/SectionProduct";
import ShippingInfo from "sections/storesPage/ShippingInfo";
import SectionSuccessModal from "sections/itemsPage/SectionSuccessModal";
import SectionDetails from "sections/itemsPage/SectionDetails";
import SectionOrdinals from "sections/itemsPage/SectionOrdinals";

import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import CustomAccordion from "components/CustomAccordion";
import SectionBox from "components/SectionBox";

import useProductType from "hooks/useProductType";
import useRedeemInfo from "hooks/useRedeemInfo";

import { PURPOSES_TYPES } from "enums/purposesTypes";
import { CHAIN_IDS } from "enums/chainIds";
import { STORE_VERSIONS } from "enums/storeTypes";
import { PAGE_TYPES } from "enums/pageTypes";
import { STATUS_PENDING } from "enums/status";
import { USER_EXTENSIONS } from "enums/userExtensions";
import { BUNDLE_EXTENSIONS } from "enums/bundleExtensions";

import { Metadata } from "interfaces/Metadata.interface";

import { API_ROUTES } from "config/routes";
import { ETHER_ADDRESS_O } from "config/addresses";

import { isAddressesEquals } from "utils/blockchain";
import { isAvailableRedeem, isCustodialItem } from "utils/redeem";
import { resetContainerPaddings } from "utils/styles";

import BundleExtensionService from "services/bundleExtensions/BundleExtensionService";
import UserExtensionService from "services/userExtensions/UserExtensionService";

import fetcher from "lib/fetchJson";

interface ProductViewProps {
  productData: Product & {
    bundle: Bundle & {
      metadata: Metadata;
      bundleExtensions: BundleExtension[];
      client: Client & {
        invoice: Pick<Invoice, "email">;
      };
    };
    codeRequired?: boolean;
  };
  claimings:
    | Pick<
        Claiming,
        | "updatedAt"
        | "purpose"
        | "transactionHash"
        | "chainId"
        | "nickname"
        | "metamaskAddress"
        | "size"
        | "dclContractAddress"
        | "dclTokenId"
        | "productionStatus"
      >[]
    | null;
  metadata: Metadata;
  owner: string | null;
  itemHistory: ItemHistory[] | null;
  mintTransactionHash: string;
  itemChainId: CHAIN_IDS;
  pageType: PAGE_TYPES;
  nickname?: string;
  isCards?: boolean;
  canMint?: boolean;
  airdropInfo?: {
    isOutsideCustodial: boolean;
    nonCustodialAddress: string;
    isClaimed: boolean;
    info?: (Airdrops & { explorerLink: string }) | null;
  };
  solAirdropInfo?: {
    explorerLink: string;
  };
}

const ProductView: FC<ProductViewProps> = ({
  productData,
  owner,
  itemHistory,
  claimings,
  mintTransactionHash,
  metadata,
  itemChainId,
  pageType,
  nickname = "",
  isCards = false,
  canMint = false,
  airdropInfo,
  solAirdropInfo,
}) => {
  const metamaskAddress = useAppSelector(selectAddress);
  const custodialAddress = useAppSelector(selectCustodialAddress);
  const isOwner =
    isAddressesEquals(metamaskAddress, owner) ||
    isAddressesEquals(custodialAddress, owner);

  const {
    redeemInfo,
    isPhysicalRedeemed,
    isDecentralandRedeemed,
    isLoading: isRedeemProposesLoading,
  } = useRedeemInfo(productData.contractAddress, productData.tokenId, isOwner);
  const productType = useProductType(metadata.attributes);
  const { data: poapEvent } = useQuery({
    queryKey: ["poap-item", owner],
    queryFn: async () => {
      const response = await fetcher<{ data: Event }>(
        `${API_ROUTES.POAP_USER_ITEM}/${owner}`,
      );
      return response.data;
    },
    enabled: !!owner && owner !== ETHER_ADDRESS_O,
  });

  const { data: userExtensions } = useQuery({
    queryKey: ["user-extension", owner],
    queryFn: async () => {
      const response = await fetcher<{ data: UserExtension[] }>(
        `${API_ROUTES.USER_EXTENSIONS_ITEM}/${owner}`,
      );
      return response.data;
    },
    enabled: !!owner && owner !== ETHER_ADDRESS_O,
  });

  const userExtensionService = new UserExtensionService({
    extensionsList: userExtensions || [],
  });

  const allLinks = userExtensionService.getConfig(USER_EXTENSIONS.LINKS)?.links;
  const links = (allLinks ?? []).filter((link) => link.url);

  const isMinted = owner && owner !== ETHER_ADDRESS_O;

  const filteredAttributes = metadata.attributes?.filter(
    (f) => f.trait_type !== "Uniqly" && f.value !== "?" && f.value !== "",
  );

  const extensions = productData.bundle.bundleExtensions;
  const bundleExtensionService = new BundleExtensionService({
    extensionsList: extensions,
    storeType: productData.bundle.client.storeType as STORE_VERSIONS,
    pageType,
  });

  // PHYSICAL
  const isPhysicalClaimed = !!redeemInfo?.find(
    (claiming) => claiming.purpose === PURPOSES_TYPES.PHYSICAL,
  )?.isRedeemed;

  const physicalClaiming = claimings?.find(
    (claiming) => claiming.purpose === PURPOSES_TYPES.PHYSICAL,
  );

  // DCL
  const isDecentralandClaimed = !!redeemInfo?.find(
    (claiming) => claiming.purpose === PURPOSES_TYPES.DECENTRALAND,
  )?.isRedeemed;
  const decentralandClaiming = claimings?.find(
    (claiming) => claiming.purpose === PURPOSES_TYPES.DECENTRALAND,
  );

  // Decentraland
  const isDclClaimingAvailable = isAvailableRedeem(
    redeemInfo,
    PURPOSES_TYPES.DECENTRALAND,
  );

  const isCustodialOwner = useMemo(
    () =>
      isCustodialItem(redeemInfo, PURPOSES_TYPES.PHYSICAL) ||
      isCustodialItem(redeemInfo, PURPOSES_TYPES.PHYSICAL_TO_NFT),
    [redeemInfo],
  );

  return (
    <Box>
      <GridContainer
        rowSpacing={2}
        columnSpacing={4}
        sx={{ mt: { xs: -2.5, sm: 0 } }}
      >
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.PRODUCT) && (
          <GridItem xs={12} mb={2}>
            <Box sx={{ mx: resetContainerPaddings }} className="uniqly-section">
              <Container>
                <SectionProduct
                  metadata={metadata}
                  isOwner={isOwner}
                  pageType={pageType}
                  ownerAddress={owner}
                  nickname={nickname}
                  isPhysicalClaimed={isPhysicalClaimed}
                  physicalClaiming={physicalClaiming}
                  itemChainId={itemChainId ?? productData.chainId}
                  mintTransactionHash={mintTransactionHash}
                  bgColor={productData.bundle.model?.bgColor}
                  tokenId={productData.tokenId}
                >
                  {bundleExtensionService.isActive(
                    BUNDLE_EXTENSIONS.ATTRIBUTES,
                  ) &&
                  filteredAttributes &&
                  filteredAttributes.length > 0 ? (
                    <GridItem xs={12} mt={3}>
                      <SectionAttributes attributes={metadata?.attributes} />
                    </GridItem>
                  ) : null}
                </SectionProduct>
              </Container>
            </Box>
          </GridItem>
        )}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.POAP) &&
          poapEvent && (
            <GridItem xs={12}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion disableGutters title="POAP" initialOpen>
                    <SectionPoap poapEvent={poapEvent} canClaim={canMint} />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          )}
        {bundleExtensionService.isActive(
          BUNDLE_EXTENSIONS.CHRIS_LOST_HIS_ITEMS,
        ) && (
          <GridItem xs={12}>
            <Box sx={{ mx: resetContainerPaddings }} className="uniqly-section">
              <Container>
                <CustomAccordion
                  disableGutters
                  title={
                    bundleExtensionService.getConfig(
                      BUNDLE_EXTENSIONS.CHRIS_LOST_HIS_ITEMS,
                    )?.title || "Chris Lost His Items"
                  }
                  initialOpen
                >
                  <SectionChrisLostHisItems
                    canClaim={canMint}
                    contractAddress={productData.contractAddress}
                    tokenId={productData.tokenId}
                    hash={productData.hash || ""}
                    description={
                      bundleExtensionService.getConfig(
                        BUNDLE_EXTENSIONS.CHRIS_LOST_HIS_ITEMS,
                      )?.description
                    }
                    privacy={
                      bundleExtensionService.getConfig(
                        BUNDLE_EXTENSIONS.CHRIS_LOST_HIS_ITEMS,
                      )?.privacy
                    }
                    emailPrompt={
                      bundleExtensionService.getConfig(
                        BUNDLE_EXTENSIONS.CHRIS_LOST_HIS_ITEMS,
                      )?.emailPrompt
                    }
                    buttonText={
                      bundleExtensionService.getConfig(
                        BUNDLE_EXTENSIONS.CHRIS_LOST_HIS_ITEMS,
                      )?.buttonText
                    }
                    successMessage={
                      bundleExtensionService.getConfig(
                        BUNDLE_EXTENSIONS.CHRIS_LOST_HIS_ITEMS,
                      )?.successMessage
                    }
                  />
                </CustomAccordion>
              </Container>
            </Box>
          </GridItem>
        )}
        {bundleExtensionService.isActive(
          BUNDLE_EXTENSIONS.FAROKH_LOST_HIS_ITEMS,
        ) && (
          <GridItem xs={12}>
            <Box sx={{ mx: resetContainerPaddings }} className="uniqly-section">
              <Container>
                <CustomAccordion
                  disableGutters
                  title="Farokh Lost His Items"
                  initialOpen
                >
                  <SectionFarokhLostHisItems
                    canClaim={canMint}
                    contractAddress={productData.contractAddress}
                    tokenId={productData.tokenId}
                    hash={productData.hash || ""}
                  />
                </CustomAccordion>
              </Container>
            </Box>
          </GridItem>
        )}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.POAP_CREATE) &&
          !poapEvent && (
            <GridItem xs={12}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title="SET UP YOUR POAP"
                    initialOpen
                  >
                    <SectionPoapCreate />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          )}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.MINT) &&
          (isOwner || !Boolean(owner)) &&
          productData.bundle.client.nfcContractAddress && (
            <GridItem xs={12}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    initialOpen
                    title={
                      bundleExtensionService.getConfig(BUNDLE_EXTENSIONS.MINT)
                        ?.tabTitle || "NFT & BLOCKCHAIN"
                    }
                    boxProps={{ className: "uniqly-accordion" }}
                  >
                    <SectionMint
                      contractAddress={productData.contractAddress}
                      nfcContractAddress={
                        productData.bundle.client.nfcContractAddress
                      }
                      tokenId={productData.tokenId}
                      isNFCMinted={Boolean(owner)}
                      hash={productData.hash || ""}
                      isPinCodeRequired={productData.codeRequired || false}
                      isCards={isCards}
                      canMint={canMint}
                      isMinting={productData.nfcStatus === STATUS_PENDING}
                      description={
                        bundleExtensionService.getConfig(BUNDLE_EXTENSIONS.MINT)
                          ?.description || undefined
                      }
                      itemName={productData.bundle.name}
                      nicknameLabel={
                        bundleExtensionService.getConfig(BUNDLE_EXTENSIONS.MINT)
                          ?.nicknameLabel
                      }
                      buttonText={
                        bundleExtensionService.getConfig(BUNDLE_EXTENSIONS.MINT)
                          ?.buttonText
                      }
                      buttonTextColorHex={
                        bundleExtensionService.getConfig(BUNDLE_EXTENSIONS.MINT)
                          ?.buttonTextColorHex
                      }
                      mintTypes={
                        bundleExtensionService.getConfig(BUNDLE_EXTENSIONS.MINT)
                          ?.mintTypes
                      }
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          )}
        {/* Enable later */}
        {/* <GridItem xs={12}>
          <Box sx={{ mx: resetContainerPaddings }} className="uniqly-section">
            <Container>
              <CustomAccordion
                disableGutters
                initialOpen
                title="⭐ Tapping NFCs (Win 1 ETH!)"
              >
                <SectionTappingNFCs
                  item={metadata.name}
                  isMinted={!!isMinted}
                  contractAddress={productData.contractAddress}
                  tokenId={productData.tokenId}
                />
              </CustomAccordion>
            </Container>
          </Box>
        </GridItem> */}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.DECENTRALAND) &&
          (isDclClaimingAvailable ||
            (isDecentralandClaimed && isOwner) ||
            (!!decentralandClaiming && isOwner)) && (
            <GridItem xs={12}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion disableGutters title="METAVERSE">
                    <SectionClaimingDecentraland
                      contractAddress={productData.contractAddress}
                      tokenId={productData.tokenId}
                      productType={productType}
                      isOwner={isOwner}
                      decentralandClaiming={decentralandClaiming}
                      isRedeemedAlready={isDecentralandRedeemed}
                      isCustodialOwner={isCustodialOwner}
                      itemName={productData.bundle.name}
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          )}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.PARTNER_CONTACT) &&
          productData.bundle.client?.invoice?.email && (
            <GridItem xs={12}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <SectionBox>
                    <SectionStorePartnerContact
                      email={productData.bundle.client.invoice.email}
                    />
                  </SectionBox>
                </Container>
              </Box>
            </GridItem>
          )}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.SHIPPING_INFO) &&
          productData.bundle?.client?.storeType && (
            <GridItem xs={12}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <SectionBox>
                    <ShippingInfo
                      isRedeemed={isPhysicalClaimed}
                      storeType={
                        productData.bundle.client.storeType as STORE_VERSIONS
                      }
                      minShipmentDays={productData.bundle.minShipmentDays}
                      maxShipmentDays={productData.bundle.maxShipmentDays}
                    />
                  </SectionBox>
                </Container>
              </Box>
            </GridItem>
          )}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.REDEEM) && (
          <GridItem xs={12}>
            <Box sx={{ mx: resetContainerPaddings }} className="uniqly-section">
              <Container>
                <SectionBox>
                  <SectionClaimingPhysical
                    contractAddress={productData.contractAddress}
                    tokenId={productData.tokenId}
                    hash={productData.hash || ""}
                    isOwner={isOwner}
                    redeemLimitDate={productData.bundle.redeemLimitDate}
                    redeemInfo={redeemInfo}
                    isPhysicalRedeemed={
                      isPhysicalRedeemed && !!physicalClaiming
                    }
                    isRedeemProposesLoading={isRedeemProposesLoading}
                  />
                </SectionBox>
              </Container>
            </Box>
          </GridItem>
        )}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.DESCRIPTION) && (
          <GridItem xs={12}>
            <Box sx={{ mx: resetContainerPaddings }} className="uniqly-section">
              <Container>
                <CustomAccordion disableGutters title="DESCRIPTION" initialOpen>
                  <SectionDescription
                    html={
                      productData.bundle.config?.descriptionHtml ||
                      bundleExtensionService.getConfig(
                        BUNDLE_EXTENSIONS.DESCRIPTION,
                      )?.html ||
                      ""
                    }
                  />
                </CustomAccordion>
              </Container>
            </Box>
          </GridItem>
        )}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.TICKET) &&
          isMinted && (
            <GridItem xs={12}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title={
                      bundleExtensionService.getConfig(BUNDLE_EXTENSIONS.TICKET)
                        ?.title || "Ticket"
                    }
                    titleTypographyProps={{
                      sx: {
                        textTransform: "uppercase",
                      },
                    }}
                  >
                    <SectionTicket
                      html={
                        bundleExtensionService.getConfig(
                          BUNDLE_EXTENSIONS.TICKET,
                        )?.html || ""
                      }
                      imageUrl={
                        productData.bundle.config?.ticketImg ||
                        bundleExtensionService.getConfig(
                          BUNDLE_EXTENSIONS.TICKET,
                        )?.img ||
                        undefined
                      }
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          )}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.DETAILS) && (
          <GridItem xs={12}>
            <Box sx={{ mx: resetContainerPaddings }} className="uniqly-section">
              <Container>
                <CustomAccordion
                  disableGutters
                  title="Details"
                  titleTypographyProps={{
                    sx: {
                      textTransform: "uppercase",
                    },
                  }}
                >
                  <SectionDetails
                    details={productData.bundle.config?.details || []}
                  />
                </CustomAccordion>
              </Container>
            </Box>
          </GridItem>
        )}
        {bundleExtensionService
          .getAllConfigsByName(BUNDLE_EXTENSIONS.WE_COUNTDOWN)
          .map((config, index) => (
            <GridItem xs={12} key={index}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title={config?.title || "Countdown"}
                    initialOpen
                  >
                    <SectionWeCountdown
                      countdownTo={config.countdownTo}
                      team1Logo={config.team1Logo}
                      team2Logo={config.team2Logo}
                      details={config.details}
                      description={config.description}
                      displayEmailForm={config.displayEmailForm}
                      additionalButton={config.additionalButton}
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          ))}
        {bundleExtensionService
          .getAllConfigsByName(BUNDLE_EXTENSIONS.EMAIL)
          .map((config, index) => (
            <GridItem xs={12} key={index}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title={config?.title || "Countdown"}
                    initialOpen
                  >
                    <SectionEmail
                      isMintGated={!!config.isMintGated}
                      mintGatedText={config.mintGatedText || ""}
                      isMinted={!!isMinted}
                      canMint={canMint}
                      bundleId={productData.bundle.id}
                      html={config.html}
                      buttonText={config.buttonText}
                      successMessage={config.successMessage}
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          ))}
        {bundleExtensionService
          .getAllConfigsByName(BUNDLE_EXTENSIONS.COLLECT_ADDRESSES)
          .map((config, index) => (
            <GridItem xs={12} key={index}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title={config?.title || "ETH Address"}
                    initialOpen
                    titleTypographyProps={{
                      sx: {
                        textTransform: "uppercase",
                      },
                    }}
                  >
                    <SectionCollectAddresses
                      canMint={canMint}
                      isMinted={!!isMinted}
                      boardId={config.boardId}
                      html={config.html}
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          ))}
        {bundleExtensionService
          .getAllConfigsByName(BUNDLE_EXTENSIONS.AIRDROP_SOL)
          .map((config, index) => (
            <GridItem xs={12} key={index}>
              <Box
                sx={{
                  mx: resetContainerPaddings,
                }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title={config.title || "Airdrop"}
                    initialOpen
                  >
                    <SectionSolAirdrop
                      html={config.html}
                      hash={productData.hash || ""}
                      buttonText={config.buttonText || "Claim"}
                      successMessage={config.successMessage || "Success"}
                      owner={owner}
                      explorerLink={solAirdropInfo?.explorerLink || ""}
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          ))}
        {bundleExtensionService
          .getAllConfigsByName(BUNDLE_EXTENSIONS.CUSTOM)
          .map((config, index) => (
            <GridItem xs={12} key={index}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title={config?.title || ""}
                    initialOpen
                    titleTypographyProps={{
                      sx: {
                        textTransform: "uppercase",
                      },
                    }}
                  >
                    <SectionDescription
                      isMintGated={!!config.isMintGated}
                      mintGatedText={config.mintGatedText || ""}
                      isMinted={!!isMinted}
                      html={config?.html || ""}
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          ))}
        {bundleExtensionService
          .getAllConfigsByName(BUNDLE_EXTENSIONS.ORDINALS)
          .map((config, index) => (
            <GridItem xs={12} key={index}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title={config?.title || ""}
                    initialOpen
                    titleTypographyProps={{
                      sx: {
                        textTransform: "uppercase",
                      },
                    }}
                  >
                    <SectionOrdinals
                      id={config.id}
                      created={config.created}
                      ownedBy={config.ownedBy}
                      creationBlock={config.creationBlock}
                      creationTx={config.creationTx}
                      creationFee={config.creationFee}
                      tags={config.tags}
                      satNumber={config.satNumber}
                      satName={config.satName}
                      satCreationBlock={config.satCreationBlock}
                      satCreationYear={config.satCreationYear}
                      inscriptions={config.inscriptions}
                      cursedInscriptions={config.cursedInscriptions}
                      satributes={config.satributes}
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          ))}
        {bundleExtensionService
          .getAllConfigsByName(BUNDLE_EXTENSIONS.AIRDROP)
          .map((config, index) => (
            <GridItem xs={12} key={index}>
              <Box
                sx={{
                  mx: resetContainerPaddings,
                }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title={config.title || "Airdrop"}
                    initialOpen
                  >
                    <SectionAirdrop
                      html={config.html}
                      canMint={canMint}
                      hash={productData.hash || ""}
                      buttonText={config.buttonText || "Claim"}
                      successMessage={config.successMessage || "Success"}
                      owner={owner}
                      airdropInfo={airdropInfo}
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          ))}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.LINKS) &&
          !!links &&
          links.length > 0 && (
            <GridItem xs={12}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title="My Links"
                    initialOpen
                    titleTypographyProps={{
                      sx: {
                        textTransform: "uppercase",
                      },
                    }}
                  >
                    <SectionLinks links={links} />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          )}
        {bundleExtensionService.isActive(BUNDLE_EXTENSIONS.HISTORY) &&
          itemHistory &&
          itemHistory.length > 0 && (
            <GridItem xs={12}>
              <Box
                sx={{ mx: resetContainerPaddings }}
                className="uniqly-section"
              >
                <Container>
                  <SectionBox>
                    <SectionHistory itemHistory={itemHistory} />
                  </SectionBox>
                </Container>
              </Box>
            </GridItem>
          )}
        {bundleExtensionService
          .getAllConfigsByName(BUNDLE_EXTENSIONS.VIDEO_SECTION)
          .map((config, index) => (
            <GridItem xs={12} key={index}>
              <Box
                sx={{
                  mx: resetContainerPaddings,
                }}
                className="uniqly-section"
              >
                <Container>
                  <CustomAccordion
                    disableGutters
                    title={config.title || "Video"}
                  >
                    <SectionVideo
                      description={config.description}
                      video={config.video || ""}
                    />
                  </CustomAccordion>
                </Container>
              </Box>
            </GridItem>
          ))}
        {bundleExtensionService
          .getAllConfigsByName(BUNDLE_EXTENSIONS.VIDEO_MODAL)
          .map((config, index) => (
            <Box key={index}>
              <SectionVideoModal
                title={config.title}
                buttonText={config.buttonText}
                video={config.video}
                buttonType={config.buttonType}
              />
            </Box>
          ))}
        <SectionSuccessModal />
      </GridContainer>
    </Box>
  );
};

export default ProductView;
